import { formatHumanName } from '@medplum/core';
import type { HumanName, Patient, Practitioner } from '@medplum/fhirtypes';

type Name = { first?: string; family?: string; display: string };

// Prefer the patient's `usual` name (e.g. preferred/chosen name) over `official` (legal),
// falling back to the first entry if neither use is set.
export const getPatientName = (patient: Patient): Name => {
  const officialName = patient.name?.find((n) => n.use === 'official');
  const usualName = patient.name?.find((n) => n.use === 'usual');

  const name = usualName ?? officialName ?? patient.name?.at(0);
  const display = formatHumanName(name) || 'Unknown patient';

  return { first: name?.given?.at(0), family: name?.family, display };
};

/** A patient's name in parts, each absent when the resource carries no value for it. */
export type PatientNameParts = {
  /** The legal first name — `official.given[0]`. Never the middle name. */
  firstName?: string;
  familyName?: string;
  /** The chosen/preferred name, from the `usual` entry. */
  preferredName?: string;
};

const trimmedValue = (value: string | undefined): string | undefined => value?.trim() || undefined;

/**
 * Read a patient's name parts off the FHIR resource.
 *
 * The legal parts come from the `official` entry and the preferred name from the `usual` one, which
 * is how the write path stores them. A `usual` entry may carry the preferred name in `text`, in
 * `given[0]`, or both, so read whichever is present rather than trusting one — externally ingested
 * patients set `given` without `text`, and older Liza patients set `text` without `given`.
 *
 * Only `given[0]` is read, so a middle name in `given[1]` never reaches a display name.
 *
 * Lives here rather than with the display formatter so both the apps and `@liza/services` can read
 * name parts off a resource — a server DTO carries the parts and lets its client choose the format.
 */
export const getPatientNameParts = (patient: Patient | null | undefined): PatientNameParts => {
  const usual = patient?.name?.find((name) => name.use === 'usual');
  // A patient with no `official` entry still has a legal name somewhere: prefer any entry that
  // isn't the preferred-name one, and fall back to the `usual` entry's own components.
  const legal = patient?.name?.find((name) => name.use === 'official') ?? patient?.name?.find((name) => name !== usual);

  return {
    firstName: trimmedValue(legal?.given?.at(0)) ?? trimmedValue(usual?.given?.at(0)),
    familyName: trimmedValue(legal?.family) ?? trimmedValue(usual?.family),
    preferredName: trimmedValue(usual?.text) ?? trimmedValue(usual?.given?.at(0)),
  };
};

export const getPractitionerName = (practitioner: Practitioner): Name => {
  const name = practitioner.name?.at(0);
  const first = name?.given?.at(0);
  const family = name?.family;
  const display = formatHumanName(name) || 'Unknown practitioner';

  return { first, family, display };
};

/**
 * Format a `HumanName` as "Last, First" (directory-style sort order).
 *
 * Differs from `@medplum/core`'s `formatHumanName`, which renders "First Last".
 * Falls back to whichever component is present, or `'Unknown'` if neither is.
 */
export const formatHumanNameLastFirst = (name: HumanName | undefined): string => {
  if (!name) return 'Unknown';
  // Coerce empty strings to `undefined` so the `??` fallback chain reaches `'Unknown'`
  // when a name has e.g. `given: []` (joins to `''`) or `family: ''`. `??` is strict
  // on nullish, so without this an empty value short-circuits the fallback.
  const given = name.given?.join(' ') || undefined;
  const family = name.family || undefined;
  if (family && given) return `${family}, ${given}`;
  return family ?? given ?? 'Unknown';
};

/** "Last, First" for a Practitioner's primary name. See `formatHumanNameLastFirst`. */
export const formatPractitionerNameLastFirst = (practitioner: Practitioner): string =>
  formatHumanNameLastFirst(practitioner.name?.at(0));

/**
 * Extract initials from first and last name
 * @param firstName - First name (or preferred name)
 * @param lastName - Last name (family name)
 * @returns Initials (e.g., "JD"), a single initial when only one name is known, or a face emoji
 *   when neither is.
 */
export const getInitials = (firstName: string | undefined | null, lastName: string | undefined | null): string => {
  const initials = ((firstName?.charAt(0) ?? '') + (lastName?.charAt(0) ?? '')).toUpperCase();
  return initials || '😊';
};
